import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CloseIcon } from "./icons";
import { useT } from "../i18n";

// Small centered dialog (export notices). Escape / backdrop / × close it; focus returns to the opener.
export function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const t = useT();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    titleRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); opener?.focus?.(); };
  }, []);
  return createPortal(
    <div className="tt-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="tt-modal-box" role="dialog" aria-modal="true" aria-labelledby="tt-modal-title">
        <div className="tt-modal-head">
          <h3 id="tt-modal-title" ref={titleRef} tabIndex={-1}>{title}</h3>
          <button className="icon-btn" onClick={onClose} aria-label={t("links.close")} title={t("links.close")}><CloseIcon size={18} /></button>
        </div>
        <div className="tt-modal-body">{children}</div>
        {footer && <div className="tt-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
